import { Box, IconButton, Typography } from "@material-ui/core";
import { Form, Formik, useFormikContext } from "formik";
import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import Error from "../../../components/Error";
import { SectionRoot } from "../../../components/flexible/SectionRoot";
import { Matched } from "../../../components/icons/ConditionStatus/Matched";
import { Pending } from "../../../components/icons/ConditionStatus/Pending";
import { Unmatched } from "../../../components/icons/ConditionStatus/Unmatched";
import { buttonBackgroundLite, positiveDelta } from "../../../hooks/use-map-theme-styles";
import { useDynamicFlexForm } from "../../../hooks/useDynamicFlexForm";
import { FlexContext } from "../../../models/flexible/flex-context";
import { createSubmittedFormData } from "../../../models/flexible/form-submission-data";
import { TypedNegotiationRound } from "../../../models/flexible/typed-negotiation-round";
import { Case, FlexEntityType, NegotiationRoundStatus } from "../../../models/generated/graphql";
import { negotiationRoundValuesState } from "../../../store/flexible";
import { firstLetterUppercase } from "../../../utils/first-letter-uppercase";
import LogToSentry, { SentryErrorLevels, SentryStatusCodes } from "../../../components/SentryLog";

export interface INegotiationRoundModel {
	source: TypedNegotiationRound;
	data: any;
	isChanged: boolean;
	isValid: boolean;
	isSelected: boolean;
}

interface NegotiationDataChangesEffectProps {
	negotiationRound: TypedNegotiationRound;
	handleChanges: (isChanged: boolean, data: any, isValid: boolean) => void;
	context: FlexContext;
	initialValues: any;
	initialData: any;
}

function NegotiationDataChangesEffect(props: NegotiationDataChangesEffectProps) {
	const { values, isValid, validateForm, setValues } = useFormikContext();
	const [actualInitialValues, setActualInitialValues] = useState(values);
	const [isRevalidate, setRevalidate] = useState(false);
	const setNegotiationRoundValues = useSetRecoilState(negotiationRoundValuesState);

	useEffect(() => {
		setTimeout(() => {
			setRevalidate(true);
		}, 0);
	}, []);

	useEffect(() => {
		validateForm();
		setActualInitialValues(values);
	}, [isRevalidate]);

	useEffect(() => {
		props.handleChanges(
			JSON.stringify(values) !== JSON.stringify(actualInitialValues),
			createSubmittedFormData(props.context, values, props.initialData),
			isValid
		);
		setNegotiationRoundValues((prevVal) => ({ ...prevVal, [props.negotiationRound.id]: values }));
	}, [actualInitialValues, values, isValid, setNegotiationRoundValues]);

	useEffect(() => {
		setValues(props.initialValues);
	}, [props.initialValues]);

	return null;
}

function NegotiationRoundComponent(props: {
	negotiationRound: TypedNegotiationRound;
	handleChanges: (isChanged: boolean, data: any, isValid: boolean) => void;
	caseInstance: Case;
}) {
	const deserializedData = props.negotiationRound.data;
	const { responseData, initialValues, validationSchema, context, error } = useDynamicFlexForm(
		deserializedData.schema,
		deserializedData.data,
		props.negotiationRound.status === NegotiationRoundStatus.InReview,
		props.caseInstance,
		props.negotiationRound.source.type as unknown as FlexEntityType
	);

	if (error) {
		return (
			<>
				<Error />
				<LogToSentry level={SentryErrorLevels.Warning} errorLog={error} statusCode={SentryStatusCodes.InternalServerError} />
			</>
		);
	}

	if (!responseData || !context) {
		return <></>;
	}

	return (
		<Fragment>
			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={() => undefined}
				validateOnMount
				validateOnChange
			>
				<Form>
					<Fragment>
						<NegotiationDataChangesEffect
							initialData={responseData}
							initialValues={initialValues}
							context={context}
							negotiationRound={props.negotiationRound}
							handleChanges={props.handleChanges}
						/>
						{context?.sectionControllers.map((s, si) => (
							<SectionRoot key={si} controller={s} parentPath={""} />
						))}
					</Fragment>
				</Form>
			</Formik>
		</Fragment>
	);
}

interface INegotiationsProps {
	caseInstance: Case;
	roundModels: Array<INegotiationRoundModel>;
	handleChanges: (id: string, isChanged: boolean, data: any, isValid: boolean) => void;
	handleSelect: (id: string) => void;
}

export function Negotiations(props: INegotiationsProps) {
	const { t } = useTranslation();
	const setNegotiationRoundValues = useSetRecoilState(negotiationRoundValuesState);

	useEffect(() => {
		setNegotiationRoundValues({});
	}, [setNegotiationRoundValues]);

	function statusIcon(status: NegotiationRoundStatus) {
		switch (status) {
			case NegotiationRoundStatus.Accepted:
			case NegotiationRoundStatus.AcceptedWithoutReview:
				return <Matched />;

			case NegotiationRoundStatus.Rejected:
				return <Unmatched />;

			case NegotiationRoundStatus.InReview:
				return <Pending />;
		}
	}

	return (
		<Box>
			<Box display="flex" alignItems="center">
				{props.roundModels.map((model, index, rounds) => (
					<Box key={index} display="flex" mb={2}>
						<IconButton
							aria-label="select round"
							size="small"
							onClick={() => props.handleSelect(model.source.id)}
							style={{
								backgroundColor: model.isSelected ? buttonBackgroundLite : undefined,
								borderRadius: "10px",
							}}
						>
							<Box display="flex" flexDirection="column" alignItems="center">
								<Box>{statusIcon(model.source.status)}</Box>
								<Typography variant="caption" color={model.isSelected ? "textPrimary" : "textSecondary"}>
									{firstLetterUppercase(t("round"))} {index + 1}
								</Typography>
							</Box>
						</IconButton>
						{index < rounds.length - 1 && (
							<div
								style={{
									margin: "10px 2px 0 2px",
									borderTop: "1px solid black",
									width: "50px",
									borderColor: positiveDelta,
								}}
							></div>
						)}
					</Box>
				))}
			</Box>
			<Box>
				{props.roundModels.map((model, index) => (
					<Box key={index} width="100%" hidden={!model.isSelected}>
						<NegotiationRoundComponent
							negotiationRound={model.source}
							handleChanges={(isChanged: boolean, data: any, isValid: boolean) =>
								props.handleChanges(model.source.id, isChanged, data, isValid)
							}
							caseInstance={props.caseInstance}
						/>
					</Box>
				))}
			</Box>
		</Box>
	);
}

export default React.memo(Negotiations);
